import React, { useEffect, useRef } from 'react';

import Icon from 'components/shared/Icon/Icon';
import Modal from 'components/shared/Modal/Modal';
import Button from 'components/shared/Button/Button';
import ErrorBoundary from 'components/shared/Error/Boundary';
import { useSubModal } from 'components/shared/Modal/components/SubModal/lib/SubModalContext';

import TagCreateSheet from './components/TagCreateSheet';
import { useTagCreateForm } from './lib/useTagCreateForm';
import TagCreateFields from './components/TagCreateFields';

const TagCreateModal = ({ isOpen, handleClose, name = '', kind, addNewTagFromCreated }) => {
  const {
    formik,
    selectedColor,
    isLoading,
    resetTagForm,
    handleChange,
    handleSelectColor,
    handleCreate,
    isBlocked,
    blockReason,
    isPrivateBlocked,
    privateBlockReason
  } = useTagCreateForm({ kind, name, addNewTagFromCreated, handleClose });

  useEffect(() => {
    if (!isOpen) return;
    resetTagForm();
  }, [isOpen]);

  return (
    <Modal
      isDirty={formik.dirty}
      isCentered
      disableHeader
      isOpen={isOpen}
      handleClose={handleClose}
      onAfterClose={resetTagForm}
      footer={
        <div className="flex w-full items-center justify-between bg-white">
          <Button text="Close" variant="tertiary" onClick={handleClose} size="small" />
          <Button
            text="Create"
            onClick={handleCreate}
            size="small"
            isLoading={isLoading}
            isDisabled={isBlocked}
            tooltip={blockReason}
            tooltipPlacement="top"
            data-qa="tag-create-btn"
          />
        </div>
      }>
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-[6px]">
          <Icon icon="tag-mono" size={24} color="primary" data-qa="new-tags-btn" />
          <span className="text-lg font-500 leading-7 text-primary-900">Create tag</span>
        </div>
        <TagCreateFields
          formik={formik}
          selectedColor={selectedColor}
          onChange={handleChange}
          onSelectColor={handleSelectColor}
          isPrivateBlocked={isPrivateBlocked}
          privateBlockReason={privateBlockReason}
        />
      </div>
    </Modal>
  );
};

// Inside an open shared Modal, present through its SubModal instead of stacking a
// second overlay; the form itself mounts fresh per open inside TagCreateSheet.
const TagCreateSubModal = ({ isOpen, handleClose, name = '', kind, addNewTagFromCreated }) => {
  const { openSubModal, closeSubModal } = useSubModal();

  const propsRef = useRef({});
  propsRef.current = { kind, name, addNewTagFromCreated, handleClose };

  // Holds this sheet's stack id while open, so closing removes our own entry rather
  // than whatever sheet is currently on top.
  const openedRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      if (openedRef.current) {
        closeSubModal(openedRef.current);
        openedRef.current = null;
      }
      return undefined;
    }

    openedRef.current = openSubModal({
      title: 'Create tag',
      footer: false,
      className: 'bg-white !p-0',
      content: <TagCreateSheet propsRef={propsRef} />,
      actions: { onClose: () => propsRef.current.handleClose?.() }
    });

    return () => {
      if (openedRef.current) {
        closeSubModal(openedRef.current);
        openedRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return null;
};

/**
 * @param {import('./lib/propTypes').TagsCreateProps} props
 */

const TagCreate = (props) => {
  const { openSubModal } = useSubModal();

  return (
    <ErrorBoundary>
      {openSubModal ? <TagCreateSubModal {...props} /> : <TagCreateModal {...props} />}
    </ErrorBoundary>
  );
};

export default TagCreate;
